import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useSession } from './auth/session';
import { Coquille } from './composants/Coquille';
import { Chargement, Message } from './composants/elements';
import { libelleRole } from './composants/roles';
import { Accueil } from './pages/Accueil';
import { Administration } from './pages/Administration';
import { Connexion } from './pages/Connexion';
import { Dashboards } from './pages/Dashboards';
import { EcartRadarPage } from './pages/EcartRadar';
import { EquipeDetailPage } from './pages/EquipeDetail';
import { FichePersonnePage } from './pages/FichePersonne';
import { MaFiche } from './pages/MaFiche';
import { QuiConnait } from './pages/QuiConnait';
import { SuiviPage } from './pages/Suivi';

/** Un garde par niveau de rôle. L'API vérifie de nouveau : ceci n'est que l'écran. */
function Reserve({
  role,
  children,
}: {
  role: 'encadrement' | 'admin';
  children: ReactNode;
}): JSX.Element {
  const session = useSession();
  if (session.estAuMoins(role)) return <>{children}</>;

  return (
    <div className="contenu section">
      <Message variante="attention">
        This page is restricted to {role === 'admin' ? 'administrators' : 'management'}. Your
        current role is “{session.moi ? libelleRole(session.moi.personne.role) : 'unknown'}”.
      </Message>
    </div>
  );
}

export function Application(): JSX.Element {
  const session = useSession();

  if (session.enCours) {
    return (
      <div className="contenu">
        <Chargement quoi="Signing in" />
      </div>
    );
  }

  if (!session.moi) return <Connexion />;

  return (
    <Coquille>
      <Routes>
        <Route path="/" element={<Accueil />} />
        <Route path="/ma-fiche" element={<MaFiche />} />
        <Route path="/ma-fiche/:trimestre" element={<MaFiche />} />
        <Route path="/qui-connait" element={<QuiConnait />} />
        <Route
          path="/dashboards"
          element={
            <Reserve role="encadrement">
              <Dashboards />
            </Reserve>
          }
        />
        <Route
          path="/ecart-radar"
          element={
            <Reserve role="encadrement">
              <EcartRadarPage />
            </Reserve>
          }
        />
        <Route
          path="/suivi"
          element={
            <Reserve role="encadrement">
              <SuiviPage />
            </Reserve>
          }
        />
        <Route
          path="/personnes/:id"
          element={
            <Reserve role="encadrement">
              <FichePersonnePage />
            </Reserve>
          }
        />
        {/* L'ordre compte : /administration/* est un attrape-tout qui masquerait
            cette route s'il était déclaré avant elle. `new` matche `:id`
            comme n'importe quelle autre valeur — pas de route séparée pour
            la création. */}
        <Route
          path="/administration/equipes/:id"
          element={
            <Reserve role="admin">
              <EquipeDetailPage />
            </Reserve>
          }
        />
        <Route
          path="/administration/*"
          element={
            <Reserve role="admin">
              <Administration />
            </Reserve>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Coquille>
  );
}
